import { inject, NgModule } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  RouterModule,
  RouterStateSnapshot,
  Routes,
} from '@angular/router';
import { CompletedComponent } from './pages/questionnaire/completed/completed.component';
import { WelcomeComponent } from './pages/questionnaire/welcome/welcome.component';
import { QuestionsComponent } from './pages/questionnaire/questions/questions.component';
import { DetailsComponent } from './pages/questionnaire/details/details.component';
import { InviteLinkLoadingComponent } from './pages/questionnaire/invite-link-loading/invite-link-loading.component';
import { NotFoundComponent } from './pages/error/not-found/not-found.component';
import { LoginComponent } from './pages/login/login.component';
import { AuthGuardService } from './services/auth-guard.service';
import { QuestionnaireUnavailableComponent } from './pages/questionnaire/questionnaire-unavailable/questionnaire-unavailable.component';
import { TwoFactorComponent } from './pages/two-factor/two-factor.component';
import { RegisterComponent } from './pages/register/register.component';
import { ResetPasswordComponent } from './pages/reset-password/reset-password.component';
import { EmailVerificationComponent } from './pages/email-verification/email-verification.component';
import { ForgotPasswordComponent } from './pages/forgot-password/forgot-password.component';
import { OauthLoginComponent } from './pages/oauth/oauth-login/oauth-login.component';
import { EmailVerificationSetupComponent } from './pages/email-verification-setup/email-verification-setup.component';
import { needCurrentQuestionGuard } from './guards/need-current-question.guard';
import { adminRoutes } from './pages/admin/admin.routes';
import { PrivacyPolicyComponent } from './components/help/privacy-policy/privacy-policy.component';
import { TermsAndConditionsComponent } from './components/help/terms-and-conditions/terms-and-conditions.component';

const canActivateAuth: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  return inject(AuthGuardService).canActivate(route, state);
};

const routes: Routes = [
  { path: '', component: LoginComponent },
  { path: 'privacy-policy', component: PrivacyPolicyComponent },
  { path: 'terms-and-conditions', component: TermsAndConditionsComponent },
  {
    path: 'release-announcements',
    loadComponent: () =>
      import('./pages/release-announcements/release-announcements.component').then(
        (m) => m.ReleaseAnnouncementsComponent,
      ),
  },
  {
    path: 'notifications',
    canActivate: [canActivateAuth],
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/notifications/notifications.component').then(
            (m) => m.NotificationsComponent,
          ),
      },
      {
        path: 'preferences',
        loadComponent: () =>
          import('./pages/notifications/notification-preferences.component').then(
            (m) => m.NotificationPreferencesComponent,
          ),
      },
    ],
  },
  {
    path: 'details',
    component: DetailsComponent,
    canActivate: [canActivateAuth],
  },
  { path: 'welcome', component: WelcomeComponent },
  {
    path: 'questions',
    component: QuestionsComponent,
    canActivate: [needCurrentQuestionGuard],
  },
  { path: 'questions/:guid', component: QuestionsComponent },
  { path: 'completed', component: CompletedComponent },
  { path: 'take-questionnaire', component: InviteLinkLoadingComponent },
  {
    path: 'questionnaire-unavailable',
    component: QuestionnaireUnavailableComponent,
  },
  {
    path: 'verify-email',
    component: EmailVerificationComponent,
  },
  { path: 'admin/login', component: LoginComponent },
  { path: 'admin/register', component: RegisterComponent },
  { path: 'admin/reset-password', component: ResetPasswordComponent },
  { path: 'admin/forgot-password', component: ForgotPasswordComponent },
  { path: 'admin/oauth2/authenticate', component: OauthLoginComponent },
  {
    path: 'admin/setup-two-factor',
    component: TwoFactorComponent,
    canActivate: [canActivateAuth],
  },
  {
    path: 'admin/setup-email-verification',
    component: EmailVerificationSetupComponent,
    canActivate: [canActivateAuth],
  },
  {
    path: 'admin',
    children: adminRoutes,
    canActivate: [canActivateAuth],
  },
  { path: '**', component: NotFoundComponent },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      enableViewTransitions: true,
      bindToComponentInputs: true,
      paramsInheritanceStrategy: 'always',
      scrollPositionRestoration: 'top',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
